import React, { useState, useEffect, useContext } from 'react';
import { useSelector, useDispatch } from '../../../redux/store';
import AdminContext from '../../../Context/adminContext';
import SearchBar from '../../../Components/SearchBar/SearchBar';
import styles from './SuppliersList.module.css';
import {
  createSupplier,
  getSuppliersList,
  fetchServiceOptions,
  setServiceFilter,
  setTypeFilter,
  setSearchValue,
  setSearchInputValue,
  setCurrentPage,
  setSortColumn,
  setSortDirection,
  hasError,
  exportSuppliers
} from "../../../redux/slices/suppliers";

import Dropdown from "../../../Components/DropDownBox/Dropdown";
import ApiResponseDisplay from '../../../Components/ApiResponseDisplay/ApiResponseDisplay';
import ProjectTablePagination from '../../Projects/ProjectList/ProjectTablePagination';
import SupplierListTable from '../../../Sections/supplier/SupplierListTable';
import SupplierForm from '../../../Components/SupplierForm/SupplierForm';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { exportButtonSx } from '../../../GlobalStyles/buttonStyles';
import { Button } from '@mui/material';
import { useSnackbar } from 'notistack';

function SupplierList() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const admin = useContext(AdminContext);
  const [addNewPopUp, setAddNewPopUp] = useState(false);
  const [isResponseShown, setIsResponseShown] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const {
    currentPage,
    supplierList,
    serviceFilter,
    typeFilter,
    serviceOptions,
    typeOptions,
    searchValue,
    searchInputValue,
    supplierListLoading,
    sortDirection,
    sortColumn,
    error,
    exportStatus
  } = useSelector((state) => state.suppliers);

  const transformedServiceOptions = serviceOptions.map(service => service);
  transformedServiceOptions.unshift('All'); // Adding 'All' to the beginning

  const transformedTypeOptions = typeOptions.map(service => service);
  transformedTypeOptions.unshift('All'); // Adding 'All' to the beginning

  const totalPages = supplierList.totalRows > 0 ? Math.ceil(supplierList.totalRows / rowsPerPage) : 0;
  const currentPageValue = supplierList.totalRows > 0 ? currentPage : 0;

  useEffect(() => {
    dispatch(setSortColumn('type')); // Default sort by Type
    dispatch(setSortDirection('asc')); // Default A-Z
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchServiceOptions());
    dispatch(getSuppliersList(serviceFilter, typeFilter, searchValue, sortColumn, sortDirection, rowsPerPage, currentPage));
    setPaginationLoading(false);
  }, [typeFilter, serviceFilter, sortColumn, sortDirection, currentPage, searchValue]);

  // Show API error modal when an error is present
  useEffect(() => {
    if (error) {
      setIsResponseShown(true);
    }
  }, [error]);

  // Custom sorting function for suppliers - Type then A-Z by company name
  const getCustomSortedRows = (rows) => {
    if (!rows || rows.length === 0) return rows;
    
    // Apply client-side sorting for Type column to ensure proper Type then A-Z order
    if (sortColumn === 'type') {
      return [...rows].sort((a, b) => {
        const typeA = a.type || '';
        const typeB = b.type || '';
        const nameA = a.company || '';
        const nameB = b.company || '';
        
        // First, sort by type
        if (sortDirection === 'asc') {
          // Subcontractor first, then alphabetical by type
          if (typeA.toLowerCase() === 'subcontractor' && typeB.toLowerCase() !== 'subcontractor') {
            return -1;
          }
          if (typeB.toLowerCase() === 'subcontractor' && typeA.toLowerCase() !== 'subcontractor') {
            return 1;
          }
          
          // If both are the same type, sort alphabetically by company name
          if (typeA.toLowerCase() === typeB.toLowerCase()) {
            return nameA.localeCompare(nameB);
          }
          
          // For different types, sort types alphabetically
          return typeA.localeCompare(typeB);
        } else {
          // Descending: reverse the type order but keep company names A-Z within each type
          if (typeA.toLowerCase() === 'subcontractor' && typeB.toLowerCase() !== 'subcontractor') {
            return 1;
          }
          if (typeB.toLowerCase() === 'subcontractor' && typeA.toLowerCase() !== 'subcontractor') {
            return -1;
          }
          
          // If both are the same type, still sort alphabetically by company name (A-Z)
          if (typeA.toLowerCase() === typeB.toLowerCase()) {
            return nameA.localeCompare(nameB);
          }
          
          // For different types, sort types reverse alphabetically
          return typeB.localeCompare(typeA);
        }
      });
    }

    // For all other columns, rely on server-side sorting
    return rows;
  };

  const handleSort = (column) => {
    // Map frontend column names to backend database column names
    const columnMapping = {
      'company': 'company',
      'maincontact': 'name',
      'website': 'website', 
      'email': 'email',
      'phoneno': 'phone',
      'services': 'services',
      'type': 'type',
      'preferred': 'preferred',
      'comment': 'comment'
    };

    const backendColumn = columnMapping[column] || column;

    if (backendColumn === sortColumn) {
      dispatch(setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc'));
    } else {
      dispatch(setSortColumn(backendColumn));
      dispatch(setSortDirection('asc'));
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setPaginationLoading(true);
      dispatch(setCurrentPage(currentPage - 1));
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setPaginationLoading(true);
      dispatch(setCurrentPage(currentPage + 1));
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages && pageNumber !== currentPage) {
      setPaginationLoading(true);
      dispatch(setCurrentPage(pageNumber));
    }
  };

  const handleSearch = (event) => {
    dispatch(setSearchInputValue(event.target.value));
  };

  const handleSearchSubmit = () => {
    dispatch(setSearchValue(searchInputValue));
    dispatch(setCurrentPage(1));
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    }
  };

  const handleAddNewSupplierSubmit = async (formData) => {
    try {
      const result = await dispatch(createSupplier(formData));
      // If using redux-thunk, the thunk may return a value; when wrapped by dispatch, some setups return the value directly
      const payload = typeof result === 'object' && result?.success !== undefined ? result : result?.payload ?? result;
      const successMessage = (payload && payload.message) || 'Supplier added successfully';
      enqueueSnackbar(successMessage, { variant: 'success' });
      setAddNewPopUp(false);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to add supplier';
      enqueueSnackbar(errorMessage, { variant: 'error' });
      // Keep popup open so user can adjust input
    }
  }

  const handleServiceFilter = (service) => {
    dispatch(setServiceFilter(service));
    dispatch(setCurrentPage(1));
  };
  
  const handleTypeFilter = (type) => {
    dispatch(setTypeFilter(type));
    dispatch(setCurrentPage(1));
  };

  const handleExportSuppliers = () => {
    dispatch(exportSuppliers());
  };

  return (
    <div className={styles.SuppliersMainContent}>
      <div className={styles.SuppliersHeader}>
        <div className={styles.SuppliersTitle}>
          Suppliers List
        </div>
      </div>

      <div className={styles.SuppliersTableFilters}>
        <div className={styles.Filters}>
          <div className={styles.FilterContainer}>
            <label className={styles.FilterLabel} htmlFor='StatusFilter' style={{ fontSize: '12px' }}>Type:</label>
            <div className={styles.dropdownContainer}>
              <Dropdown options={typeOptions} initialSelectedValue={typeFilter} onValueChange={handleTypeFilter} disabled={supplierListLoading} />
            </div>

            <label className={styles.FilterLabel} htmlFor='ServiceFilter' style={{ marginLeft: '20px', fontSize: '12px' }}>Service:</label>
            <div className={styles.dropdownContainer}>
              <Dropdown options={transformedServiceOptions} initialSelectedValue={serviceFilter} onValueChange={handleServiceFilter} options_width='190%' disabled={supplierListLoading} />
            </div>
          </div>
        </div>

        <div className={styles.SearchBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {admin && (
              <div className={`${styles.AddBtn} ${supplierListLoading ? styles.AddDisabled : ''}`} onClick={!supplierListLoading ? () => setAddNewPopUp(true) : null}>
                ADD
              </div>
            )}
            <Button
              variant="contained"
              onClick={handleExportSuppliers}
              disabled={exportStatus === 'loading'}
              startIcon={<FileDownloadIcon sx={{ fontSize: '16px' }}/>} 
              sx={{ ...exportButtonSx, marginRight: '10px' }}
            >
              {exportStatus === 'loading' ? 'Exporting...' : 'Export'}
            </Button>
          </div>
          <SearchBar value={searchInputValue} onChange={handleSearch} onKeyDown={handleKeyDown} disabled={supplierListLoading} onClick={handleSearchSubmit} />
        </div>
      </div>

      {supplierListLoading ? (
        <React.Fragment>
          <SupplierListTable
            rows={[]}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            loading={supplierListLoading}
          />

          <ProjectTablePagination
            currentPage={currentPageValue}
            totalPages={totalPages}
            onPrevPage={paginationLoading ? null : handlePrevPage} // disable if loading
            onNextPage={paginationLoading ? null : handleNextPage} // disable if loading
            rowsShown={supplierList.rows.length}
            totalRows={supplierList.totalRows}
            rowsPerPage={rowsPerPage}
            loading={supplierListLoading}
            onPageChange={handlePageChange}
          />
        </React.Fragment>
      ) : (
        <React.Fragment>
          <SupplierListTable
            rows={getCustomSortedRows(supplierList.rows)}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            handleSort={handleSort}
            loading={supplierListLoading}
            onPageChange={handlePageChange}
          />

          <ProjectTablePagination
            currentPage={currentPageValue}
            totalPages={totalPages}
            onPrevPage={paginationLoading ? null : handlePrevPage} // disable if loading
            onNextPage={paginationLoading ? null : handleNextPage} // disable if loading
            rowsShown={supplierList.rows.length}
            totalRows={supplierList.totalRows}
            rowsPerPage={rowsPerPage}
            loading={supplierListLoading}
            onPageChange={handlePageChange}
          />

          {isResponseShown && error && error !== null && (
            <ApiResponseDisplay
              apiResponse={error}
              setIsResponseShown={setIsResponseShown}
              onClose={() => dispatch(hasError(null))}
            />
          )}

          {addNewPopUp && (
            <SupplierForm
              onSubmit={handleAddNewSupplierSubmit}
              onClose={() => setAddNewPopUp(false)}
            />
          )}
        </React.Fragment>
      )}
    </div>
  )
}

export default SupplierList;